/**
 * Push notification management — permission, subscription, and lifecycle.
 *
 * Uses the Web Push API to manage browser push notification subscriptions.
 * Subscriptions are sent to the backend for storage and dispatch.
 */

import { api } from './api';

// ─── VAPID Public Key ───
// This must match the VAPID key pair configured in the backend.
// In production, this should come from an environment variable.
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';

// ─── Local Storage Keys ───
const NOTIFICATION_CHOICE_KEY = 'ckd_notification_choice'; // 'granted' | 'denied' | 'later'
const NOTIFICATION_LATER_DATE_KEY = 'ckd_notification_later_date';
const NOTIFICATION_PREFS_KEY = 'ckd_notification_preferences';

// ─── Types ───

export type Weekday =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export type VetReminderLeadDays = 3 | 7 | 14;

export interface NotificationPreferences {
  monitoring_due: boolean;
  daily_log_reminder: boolean;
  weekly_weighin_reminder: boolean;
  /** Daily medication reminder(s) for the cat's active medications. */
  medication_reminder: boolean;
  daily_reminder_time: string; // HH:MM format, e.g. "20:00"
  weekly_weighin_days: Weekday[];
  weekly_weighin_time: string; // HH:MM format, e.g. "09:00"
  /** Default time for medication reminders; a medication's own reminder_time,
   *  when set, overrides this for that medication. HH:MM, e.g. "08:00". */
  medication_reminder_time: string;
  /** SubQ fluids reminder — off by default (only cats prescribed fluids want it). */
  fluids_reminder: boolean;
  fluids_reminder_time: string; // HH:MM, e.g. "18:00"
  fluids_reminder_days: Weekday[];
  vet_reminder_days_before: VetReminderLeadDays;
  /** IANA timezone name (e.g. "America/Los_Angeles"). The backend scheduler
   *  compares all reminder times in this zone, so the user receives them at
   *  the wall-clock time they configured rather than the UTC equivalent. */
  timezone: string;
}

/**
 * Best-effort detection of the user's IANA timezone via the browser. Falls
 * back to 'UTC' if `Intl` is unavailable (rare — SSR or very old browsers).
 */
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return tz;
  } catch {
    // Intl unavailable — fall through to UTC default.
  }
  return 'UTC';
}

export const DEFAULT_PREFERENCES: NotificationPreferences = {
  monitoring_due: true,
  daily_log_reminder: true,
  weekly_weighin_reminder: true,
  medication_reminder: true,
  daily_reminder_time: '20:00',
  weekly_weighin_days: ['sunday'],
  weekly_weighin_time: '09:00',
  medication_reminder_time: '08:00',
  fluids_reminder: false,
  fluids_reminder_time: '18:00',
  fluids_reminder_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
  vet_reminder_days_before: 7,
  timezone: detectTimezone(),
};

// ─── Feature Detection ───

/**
 * Check if the browser supports push notifications.
 */
export function isNotificationSupported(): boolean {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Get the current notification permission state.
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

// ─── Permission & Subscription ───

/**
 * Request notification permission from the user.
 * Returns true if permission was granted, false otherwise.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNotificationSupported()) return false;

  const result = await Notification.requestPermission();

  if (result === 'granted') {
    localStorage.setItem(NOTIFICATION_CHOICE_KEY, 'granted');
    return true;
  }

  localStorage.setItem(NOTIFICATION_CHOICE_KEY, 'denied');
  return false;
}

/**
 * Subscribe to push notifications and register the subscription with the backend.
 * Returns the PushSubscription if successful, null otherwise.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isNotificationSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription first
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // Re-register with backend in case it was lost
      await sendSubscriptionToBackend(existing);
      return existing;
    }

    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    // Send to backend for storage
    await sendSubscriptionToBackend(subscription);

    return subscription;
  } catch (err) {
    console.error('Failed to subscribe to push notifications:', err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications and remove from backend.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!isNotificationSupported()) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Remove from backend
      try {
        await api.delete('/notifications/unsubscribe');
      } catch {
        // Backend removal may fail if already removed; continue with local unsubscribe
      }

      // Unsubscribe locally
      await subscription.unsubscribe();
    }

    localStorage.setItem(NOTIFICATION_CHOICE_KEY, 'denied');
  } catch (err) {
    console.error('Failed to unsubscribe from push notifications:', err);
  }
}

// ─── Preference Management ───

/**
 * Get the stored notification preferences (local fallback).
 */
export function getStoredPreferences(): NotificationPreferences {
  // Always refresh the timezone — users travel, and we want reminders to
  // follow them rather than stick to the zone they first signed up in.
  const baseline: NotificationPreferences = {
    ...DEFAULT_PREFERENCES,
    timezone: detectTimezone(),
  };
  try {
    const stored = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (stored) {
      return { ...baseline, ...JSON.parse(stored), timezone: baseline.timezone };
    }
  } catch {
    // Corrupted localStorage — fall through to baseline.
  }
  return baseline;
}

/**
 * The user's preferences as the SERVER holds them, hydrated on load.
 *
 * The store was write-only from the client: `savePreferences` wrote to the server
 * and `getStoredPreferences` read `localStorage`, so a user signing in on a second
 * device met the defaults with a fully populated server record behind them. That is
 * the portability problem the original finding was about — moving the record off the
 * push subscription only fixed the silent 404.
 *
 * Precedence, on purpose: server, then this device's `localStorage`, then defaults.
 * WHERE THE TWO DISAGREE THE SERVER WINS, because the server copy is the one the
 * reminder scheduler actually reads — a device that shows something else is showing
 * the user a schedule they will not receive. The local copy is a fallback for a
 * failed fetch, not a competing source of truth.
 *
 * Timezone is the one exception and is always taken from THIS device: reminders
 * should follow a user who travels, not the zone they signed up in.
 *
 * Never throws: a failed fetch degrades to the local copy and the caller carries on.
 */
export async function loadPreferences(): Promise<NotificationPreferences> {
  const local = getStoredPreferences();
  try {
    const server = (await api.get('/notifications/preferences')) as
      | Partial<NotificationPreferences>
      | undefined;
    if (!server || typeof server !== 'object') return local;
    const merged: NotificationPreferences = {
      ...local,
      ...server,
      timezone: detectTimezone(),
    };
    // Cache the server's answer so the next cold start on this device opens on it
    // rather than on whatever was last saved from here.
    try {
      localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(merged));
    } catch {
      // Storage full or unavailable — the in-memory value is still correct.
    }
    return merged;
  } catch {
    return local;
  }
}

/**
 * Save notification preferences locally and to the backend.
 *
 * Local storage is written first and unconditionally, so the user's choice
 * survives even when the network doesn't. A backend failure then REJECTS rather
 * than passing silently: this call used to swallow every error, and because the
 * endpoint answered 404 for anyone without a push subscription, the settings the
 * user had just chosen never left the device and nothing said so — not the UI,
 * not the console.
 *
 * Callers are expected to surface the rejection the way the date-format setting
 * does: inline, beside the control, without blocking.
 */
export async function savePreferences(prefs: NotificationPreferences): Promise<void> {
  // Always stamp the current detected timezone — see getStoredPreferences().
  const withTz: NotificationPreferences = { ...prefs, timezone: detectTimezone() };
  localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(withTz));

  await api.put('/notifications/preferences', withTz);
}

// ─── Prompt Timing ───

/**
 * Check whether we should show the notification prompt on the dashboard.
 * Rules:
 *  - User must have visited the app 3+ times (reuses visit counter from InstallPrompt)
 *  - User hasn't already granted or permanently denied
 *  - If user chose "Maybe later", wait 7 days before re-prompting
 */
export function shouldShowNotificationPrompt(): boolean {
  if (!isNotificationSupported()) return false;

  // Already granted system permission — no need to prompt
  if (Notification.permission === 'granted') return false;

  // System-level denied — can't ask again
  if (Notification.permission === 'denied') return false;

  const choice = localStorage.getItem(NOTIFICATION_CHOICE_KEY);

  // User already enabled or explicitly denied through our UI
  if (choice === 'granted' || choice === 'denied') return false;

  // "Maybe later" — check if 7 days have passed
  if (choice === 'later') {
    const laterDate = localStorage.getItem(NOTIFICATION_LATER_DATE_KEY);
    if (laterDate) {
      const sevenDaysLater = new Date(laterDate);
      sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
      if (new Date() < sevenDaysLater) return false;
    }
  }

  // Check visit count (shared with InstallPrompt)
  const visitCount = parseInt(localStorage.getItem('ckd_visit_count') || '0', 10);
  return visitCount >= 3;
}

/**
 * Record that the user chose "Maybe later".
 */
export function dismissNotificationPrompt(): void {
  localStorage.setItem(NOTIFICATION_CHOICE_KEY, 'later');
  localStorage.setItem(NOTIFICATION_LATER_DATE_KEY, new Date().toISOString());
}

// ─── Helpers ───

/**
 * Convert a URL-safe Base64 string to a Uint8Array (for VAPID key).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Send a push subscription to the backend for storage.
 */
async function sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const prefs = getStoredPreferences();

  await api.post('/notifications/subscribe', {
    endpoint: json.endpoint,
    p256dh_key: json.keys?.p256dh ?? '',
    auth_key: json.keys?.auth ?? '',
    preferences: prefs,
  });
}
